// Engagement signals so single-page visits don't count as bounces.
// Sends additional events (heartbeats, scroll depth, CTA clicks, exit) to
// Growth Hub via the existing visitor-tracking edge function.

import { LOCAL_FUNCTIONS, TRACKING_HEADERS } from "@/integrations/supabase/growthHubClient";
import { trackGoogleEmailConversion, trackGooglePhoneConversion } from "@/lib/googleTracking";

function getIds() {
  return {
    visitor_id: localStorage.getItem("obc_visitor_id") || undefined,
    session_id: sessionStorage.getItem("obc_session_id") || undefined,
  };
}

// Module-level engagement state so every event carries scroll/time fields
const pageStart = typeof window !== "undefined" ? Date.now() : 0;
let maxScrollDepth = 0;

function currentScrollPct(): number {
  const docH = document.documentElement.scrollHeight - window.innerHeight;
  if (docH <= 0) return 0;
  const pct = Math.round((window.scrollY / docH) * 100);
  return Math.max(0, Math.min(100, pct));
}

function send(event_name: string, extra: Record<string, unknown> = {}) {
  const ids = getIds();
  if (!ids.visitor_id || !ids.session_id) return;

  const scrollPct = currentScrollPct();
  if (scrollPct > maxScrollDepth) maxScrollDepth = scrollPct;
  const timeOnPageSeconds = Math.round((Date.now() - pageStart) / 1000);

  const payload = JSON.stringify({
    action: "track_event",
    data: {
      ...ids,
      event_type: "engagement",
      event_name,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      event_source_url: window.location.href,
      // Hub bounce-clear engine requires these exact field names:
      scroll_depth_percent: scrollPct,
      max_scroll_depth: maxScrollDepth,
      time_on_page_seconds: timeOnPageSeconds,
      ...extra,
    },
  });

  // Prefer sendBeacon for unload reliability
  if (event_name === "page_exit" && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(
        LOCAL_FUNCTIONS.visitorTracking,
        new Blob([payload], { type: "application/json" })
      );
      return;
    } catch { /* fall through */ }
  }

  fetch(LOCAL_FUNCTIONS.visitorTracking, {
    method: "POST",
    headers: TRACKING_HEADERS,
    body: payload,
    keepalive: true,
  }).catch(() => { /* swallow */ });
}

let initialized = false;

function isPlainLeftClick(e: MouseEvent) {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

export function initEngagementTracking() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  // 1. Heartbeats — kills single-event "bounce" classification
  const t15 = window.setTimeout(() => send("engaged_15s"), 15_000);
  const t45 = window.setTimeout(() => send("engaged_45s"), 45_000);

  // 2. Scroll depth thresholds
  const fired = new Set<number>();
  const onScroll = () => {
    const docH = document.documentElement.scrollHeight;
    if (docH <= 0) return;
    const pct = Math.round(((window.scrollY + window.innerHeight) / docH) * 100);
    [25, 50, 75, 100].forEach((mark) => {
      if (pct >= mark && !fired.has(mark)) {
        fired.add(mark);
        send("scroll_depth", { depth_pct: mark });
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  // 3. CTA click tracking (phone, email, quote/estimate/contact buttons)
  const PHONE_TEXT_RE = /\b(call (now|us|today)|click to call|tap to call|call me back)\b/i;

  document.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement | null)?.closest("a,button,[role='button'],[data-phone-cta]") as HTMLElement | null;
    if (!el) return;
    const anchor = el instanceof HTMLAnchorElement ? el : null;
    const rawHref = anchor?.getAttribute("href") || "";
    const href = anchor?.href || rawHref;
    const text = (el.textContent || "").trim();
    const ariaLabel = el.getAttribute("aria-label") || "";

    // Detect a phone CTA even when it's not a tel: anchor: marked element,
    // wraps a tel: link, or its label says "Call Now / Call Us / etc."
    const innerTelHref = el.querySelector?.('a[href^="tel:"]')?.getAttribute("href") || "";
    const isMarkedPhoneCta = el.hasAttribute("data-phone-cta");
    const looksLikePhoneCta =
      isMarkedPhoneCta ||
      !!innerTelHref ||
      PHONE_TEXT_RE.test(text) ||
      PHONE_TEXT_RE.test(ariaLabel);

    const telHref = href.startsWith("tel:") || rawHref.startsWith("tel:")
      ? (rawHref || href)
      : innerTelHref || (isMarkedPhoneCta ? el.getAttribute("data-phone-href") || "" : "");

    if (telHref || (looksLikePhoneCta && !href.startsWith("mailto:"))) {
      send("click_phone", { href: telHref || href || undefined });
      // Never prevent/default-delay tel: navigation. Mobile browsers require the
      // dialer handoff to happen directly from the user's tap; waiting for an Ads
      // callback can cause tracking to fire but the phone app not to open.
      try {
        trackGooglePhoneConversion({
          form_id: anchor && (href.startsWith("tel:") || rawHref.startsWith("tel:")) ? "tel_link" : isMarkedPhoneCta ? "phone_cta_button" : "phone_cta_text",
          page_path: window.location.pathname,
          page_location: window.location.href,
          destination_url: telHref || href || undefined,
          value: 1,
          currency: "USD",
        });
      } catch {}
    } else if (href.startsWith("mailto:") || rawHref.startsWith("mailto:")) {
      send("click_email", { href });
      if (anchor && isPlainLeftClick(e)) {
        e.preventDefault();
        let navigated = false;
        const navigate = () => {
          if (navigated) return;
          navigated = true;
          window.location.href = rawHref || href;
        };
        let fired = false;
        try {
          fired = trackGoogleEmailConversion({
            form_id: "mailto_link",
            page_path: window.location.pathname,
            page_location: window.location.href,
            destination_url: rawHref || href,
            value: 1,
            currency: "USD",
            event_callback: navigate,
            event_timeout: 1200,
          });
        } catch {}
        window.setTimeout(navigate, fired ? 1200 : 40);
      } else {
        try { trackGoogleEmailConversion({ form_id: "mailto_link", page_path: window.location.pathname, destination_url: rawHref || href, value: 1, currency: "USD" }); } catch {}
      }
    } else if (/quote|estimate|contact|schedule|inspection/i.test(text)) {
      send("click_cta", { label: text.slice(0, 60), href: href || undefined });
    }
  }, true);


  // 4. Exit ping with time-on-page + max scroll depth
  const start = Date.now();
  let exitSent = false;
  const sendExit = () => {
    if (exitSent) return;
    exitSent = true;
    window.clearTimeout(t15);
    window.clearTimeout(t45);
    send("page_exit", {
      time_on_page_ms: Date.now() - start,
      max_scroll_pct: fired.size ? Math.max(...fired) : 0,
    });
  };
  window.addEventListener("pagehide", sendExit);
  window.addEventListener("beforeunload", sendExit);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) sendExit();
  });
}
